// QRDownloadUtils.js
import { useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { getTemplateConfig } from '../utils/templateConfig';

export class QRDownloadManager {
  constructor() {
    this.qrInstance = null;
  }

  // Generate QR code instance with all options
  generateQRInstance(formData, contentType, qrOptions) {
    const content = this.getContentData(formData, contentType);

    if (!content) {
      throw new Error('No content provided for QR code generation');
    }

    const config = this.getQRConfig(content, qrOptions);
    this.qrInstance = new QRCodeStyling(config);

    return this.qrInstance;
  }

  // Get content data based on type
  getContentData(formData, contentType) {
    switch (contentType) {
      case 'url':
        return formData.url;
      case 'text':
        return formData.text;
      case 'contact': {
        const { firstName, lastName, email, phone, organization } =
          formData.contact;

        if (!firstName && !lastName && !email && !phone) {
          return '';
        }

        return `BEGIN:VCARD
VERSION:3.0
FN:${firstName} ${lastName}
N:${lastName};${firstName};;;
ORG:${organization || ''}
EMAIL:${email}
TEL:${phone}
END:VCARD`;
      }
      default:
        return '';
    }
  }

  // Get QR configuration with template and options
  getQRConfig(content, qrOptions) {
    const baseConfig = {
      width: 1024, // High resolution for quality downloads
      height: 1024,
      type: 'canvas', // Use canvas for better download support
      data: content,
      margin: qrOptions.includeMargin ? 4 : 0,
      qrOptions: {
        errorCorrectionLevel: qrOptions.errorCorrectionLevel,
      },
      dotsOptions: {
        color: qrOptions.foregroundColor,
      },
      backgroundOptions: {
        color: qrOptions.backgroundColor,
      },
      cornersSquareOptions: {
        type: 'square',
        color: qrOptions.foregroundColor,
      },
      cornersDotOptions: {
        type: 'square',
        color: qrOptions.foregroundColor,
      },
    };

    // Apply template-specific configurations
    const templateConfig = this.getTemplateConfig(
      qrOptions.template,
      qrOptions.foregroundColor,
    );
    const finalConfig = { ...baseConfig, ...templateConfig };

    // Add logo if present
    if (qrOptions.logoFile) {
      const maxLogoSizes = { L: 0.07, M: 0.15, Q: 0.25, H: 0.35 };
      const logoSizeRatio = Math.min(qrOptions.logoSize / 100, maxLogoSizes.H);

      finalConfig.image = qrOptions.logoFile;
      finalConfig.imageOptions = {
        hideBackgroundDots: true,
        imageSize: logoSizeRatio,
        margin: 2,
        crossOrigin: 'anonymous',
      };
      // Force high error correction for logo QR codes
      finalConfig.qrOptions.errorCorrectionLevel = 'H';
    }

    return finalConfig;
  }

  // Get template-specific configuration from shared utility
  getTemplateConfig(template, foregroundColor) {
    return getTemplateConfig(template, foregroundColor);
  }

  // Download as PNG
  async downloadPNG(size = 1024, filename = 'qrcode') {
    if (!this.qrInstance) {
      throw new Error('QR instance not initialized');
    }

    return new Promise((resolve, reject) => {
      // Update size if different from current
      this.qrInstance.update({
        width: size,
        height: size,
      });

      this.qrInstance
        .download({
          name: filename,
          extension: 'png',
        })
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  // Download as JPEG
  async downloadJPEG(size = 1024, filename = 'qrcode') {
    if (!this.qrInstance) {
      throw new Error('QR instance not initialized');
    }

    return new Promise((resolve, reject) => {
      this.qrInstance.update({
        width: size,
        height: size,
      });

      this.qrInstance
        .download({
          name: filename,
          extension: 'jpeg',
        })
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  // Download as SVG
  async downloadSVG(size = 1024, filename = 'qrcode') {
    if (!this.qrInstance) {
      throw new Error('QR instance not initialized');
    }

    return new Promise((resolve, reject) => {
      this.qrInstance.update({
        width: size,
        height: size,
        type: 'svg', // Switch to SVG for this download
      });

      this.qrInstance
        .download({
          name: filename,
          extension: 'svg',
        })
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  // Download as PDF
  async downloadPDF(size = 1024, filename = 'qrcode') {
    if (!this.qrInstance) {
      throw new Error('QR instance not initialized');
    }

    try {
      // Get the canvas data
      const canvas = await this.getCanvas(size);
      const imgData = canvas.toDataURL('image/png');

      // Create PDF using a simple approach
      const pdf = await this.createPDF(imgData, size, filename);
      return pdf;
    } catch (error) {
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  }

  // Get canvas element from QR instance
  async getCanvas(size = 1024) {
    return new Promise((resolve, reject) => {
      this.qrInstance.update({
        width: size,
        height: size,
        type: 'canvas',
      });

      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      this.qrInstance.append(tempContainer);

      // Wait for render
      setTimeout(() => {
        const canvas = tempContainer.querySelector('canvas');
        if (canvas) {
          resolve(canvas);
        } else {
          reject(new Error('Canvas not found'));
        }
        document.body.removeChild(tempContainer);
      }, 100);
    });
  }

  // Simple PDF creation (you might want to use jsPDF for more features)
  async createPDF(imgData, size, filename) {
    return new Promise((resolve) => {
      // Simple PDF creation - for a more robust solution, use jsPDF
      const link = document.createElement('a');
      link.download = `${filename}.pdf`;

      // For now, we'll download as PNG and suggest user converts to PDF
      // In a real implementation, you'd use jsPDF or similar
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);

        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      };
      img.src = imgData;
    });
  }

  // Get shareable data URL
  async getDataURL(format = 'png', size = 512) {
    if (!this.qrInstance) {
      throw new Error('QR instance not initialized');
    }

    const canvas = await this.getCanvas(size);
    return canvas.toDataURL(`image/${format}`);
  }

  // Copy QR code to clipboard
  async copyToClipboard(size = 512) {
    try {
      const canvas = await this.getCanvas(size);

      // Convert canvas to blob
      return new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
          try {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob }),
            ]);
            resolve();
          } catch (error) {
            reject(error);
          }
        }, 'image/png');
      });
    } catch (error) {
      throw new Error(`Failed to copy to clipboard: ${error.message}`);
    }
  }

  // Generate filename based on content and timestamp
  generateFilename(formData, contentType) {
    const timestamp = new Date().toISOString().slice(0, 10);

    switch (contentType) {
      case 'url': {
        const domain = this.extractDomain(formData.url);
        return `qr-${domain}-${timestamp}`;
      }
      case 'text': {
        const textPreview = formData.text
          .slice(0, 20)
          .replace(/[^a-zA-Z0-9]/g, '-');
        return `qr-text-${textPreview}-${timestamp}`;
      }
      case 'contact': {
        const name =
          `${formData.contact.firstName}-${formData.contact.lastName}`.replace(
            /[^a-zA-Z0-9]/g,
            '-',
          );
        return `qr-contact-${name}-${timestamp}`;
      }
      default:
        return `qr-code-${timestamp}`;
    }
  }

  // Extract domain from URL for filename
  extractDomain(url) {
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return domain.replace(/[^a-zA-Z0-9]/g, '-');
    } catch {
      return 'website';
    }
  }
}

// Hook for using download manager
export const useQRDownload = () => {
  const managerRef = useRef(null);
  if (!managerRef.current) {
    managerRef.current = new QRDownloadManager();
  }
  const downloadManager = managerRef.current;

  const downloadQR = async (formData, contentType, qrOptions, format, size) => {
    try {
      // Generate QR instance
      downloadManager.generateQRInstance(formData, contentType, qrOptions);

      // Generate filename
      const filename = downloadManager.generateFilename(formData, contentType);

      // Download based on format
      switch (format.toLowerCase()) {
        case 'png':
          await downloadManager.downloadPNG(parseInt(size), filename);
          break;
        case 'jpg':
        case 'jpeg':
          await downloadManager.downloadJPEG(parseInt(size), filename);
          break;
        case 'svg':
          await downloadManager.downloadSVG(parseInt(size), filename);
          break;
        case 'pdf':
          await downloadManager.downloadPDF(parseInt(size), filename);
          break;
        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      return { success: true, filename };
    } catch (error) {
      console.error('Download failed:', error);
      return { success: false, error: error.message };
    }
  };

  const copyQRToClipboard = async (
    formData,
    contentType,
    qrOptions,
    size = 512,
  ) => {
    try {
      downloadManager.generateQRInstance(formData, contentType, qrOptions);
      await downloadManager.copyToClipboard(size);
      return { success: true };
    } catch (error) {
      console.error('Copy to clipboard failed:', error);
      return { success: false, error: error.message };
    }
  };

  const getShareableURL = async (
    formData,
    contentType,
    qrOptions,
    size = 512,
  ) => {
    try {
      downloadManager.generateQRInstance(formData, contentType, qrOptions);
      const dataURL = await downloadManager.getDataURL('png', size);
      return { success: true, dataURL };
    } catch (error) {
      console.error('Get shareable URL failed:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    downloadQR,
    copyQRToClipboard,
    getShareableURL,
  };
};
